import React, { useEffect, useState } from 'react';
import { useDecisionContext } from '../context/DecisionContext';
import { Decision, DecisionTree } from '../models/decisionTree';

// 添加注释功能的接口
interface DecisionNotes {
  [decisionId: string]: string;
}

interface DecisionNodeProps {
  decision: Decision;
  question: string;
  level: number;
  readOnly?: boolean;
  notes: DecisionNotes;
  onNoteChange: (decisionId: string, note: string) => void;
  onExpandDecision?: (decision: Decision, subQuestion?: string) => Promise<void>;
}

const DecisionNode: React.FC<DecisionNodeProps> = ({ 
  decision, 
  question, 
  level, 
  readOnly = false,
  notes,
  onNoteChange,
  onExpandDecision
}) => {
  const { loading } = useDecisionContext();
  const [expanded, setExpanded] = React.useState(false);
  const [isExpanding, setIsExpanding] = React.useState(false);
  const [currentNodeId, setCurrentNodeId] = React.useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [noteText, setNoteText] = useState(notes[decision.id] || '');
  const [showSubQuestionInput, setShowSubQuestionInput] = useState(false);
  const [subQuestion, setSubQuestion] = useState('');
  const [expansionError, setExpansionError] = useState<string | null>(null);
  const [lastSubmittedQuestion, setLastSubmittedQuestion] = useState<string>('');
  const [isReanalyzing, setIsReanalyzing] = useState(false);

  // 处理笔记变更
  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteText(e.target.value);
    onNoteChange(decision.id, e.target.value);
  };

  // 追踪当前正在展开的节点ID
  useEffect(() => {
    if (!loading && isExpanding) {
      setIsExpanding(false);
      setCurrentNodeId(null);
    }
  }, [loading, decision.children]);

  // 重置错误状态
  useEffect(() => {
    if (decision.children.length > 0 && expansionError) {
      setExpansionError(null);
    }
  }, [decision.children]);

  // 处理展开/收起
  const handleExpand = () => {
    if (decision.children.length > 0) {
      setExpanded(!expanded);
    } else {
      // 显示子问题输入界面
      setShowSubQuestionInput(true);
      setExpansionError(null);
    }
  };

  // 处理子问题提交
  const handleSubQuestionSubmit = async () => {
    if (!subQuestion.trim() || !onExpandDecision) return;

    // 保存当前问题用于可能的重试
    setLastSubmittedQuestion(subQuestion);
    
    setExpansionError(null);
    setIsExpanding(true);
    setCurrentNodeId(decision.id);
    setShowSubQuestionInput(false);

    try {
      await onExpandDecision(decision, subQuestion);
      // 展开成功后显示子决策
      setExpanded(true);
      // 清空输入
      setSubQuestion('');
    } catch (error) {
      console.error('展开子决策失败:', error);
      // API错误特殊处理
      const errorMessage = error instanceof Error ? error.message : '生成子决策失败，请重试';
      if (errorMessage.includes('API key expired')) {
        setExpansionError('API密钥已过期，请更新API密钥后重试。您的决策树数据没有丢失。');
      } else {
        setExpansionError(errorMessage);
      }
      setShowSubQuestionInput(true);
    } finally {
      setIsExpanding(false);
      setCurrentNodeId(null);
    }
  };

  // 重试展开决策
  const handleRetry = async () => {
    const questionToUse = lastSubmittedQuestion || subQuestion;
    if (!questionToUse.trim() || !onExpandDecision) return;
    
    setExpansionError(null);
    setIsExpanding(true);
    setCurrentNodeId(decision.id);
    
    try {
      await onExpandDecision(decision, questionToUse);
      // 展开成功后显示子决策
      setExpanded(true);
      // 清除错误
      setExpansionError(null);
      // 清空输入并隐藏输入框
      setSubQuestion('');
      setShowSubQuestionInput(false);
    } catch (error) {
      console.error('重试展开子决策失败:', error);
      // API错误特殊处理
      const errorMessage = error instanceof Error ? error.message : '生成子决策失败，请重试';
      if (errorMessage.includes('API key expired')) {
        setExpansionError('API密钥已过期，请更新API密钥后重试。您的决策树数据没有丢失。');
      } else {
        setExpansionError(errorMessage);
      }
      setShowSubQuestionInput(true);
    } finally {
      setIsExpanding(false);
      setCurrentNodeId(null);
    }
  };

  // 重新分析子决策
  const handleReanalyze = async () => {
    if (!onExpandDecision) return;
    
    // 提示用户可能会丢失现有子决策
    if (!window.confirm(`确定要重新分析"${decision.title}"的子决策吗？这将替换所有现有子决策。`)) {
      return;
    }
    
    // 提示用户输入问题
    const analysisQuestion = prompt('请输入分析问题:', '请详细分析这个决策的子选项');
    if (!analysisQuestion) return;
    
    setIsReanalyzing(true);
    try {
      await onExpandDecision(decision, analysisQuestion);
      setExpanded(true); // 展开以显示新的子决策
    } catch (error) {
      console.error('重新分析子决策失败:', error);
      alert('重新分析失败: ' + (error instanceof Error ? error.message : '未知错误'));
    } finally {
      setIsReanalyzing(false);
    }
  };

  // 共用的展开决策逻辑
  const expandDecision = async (questionText: string) => {
    if (!onExpandDecision) return;
    
    setIsExpanding(true);
    setCurrentNodeId(decision.id);
    setShowSubQuestionInput(false);
    
    try {
      await onExpandDecision(decision, questionText);
      // 展开成功后显示子决策
      setExpanded(true);
      // 清除错误
      setExpansionError(null);
      // 清空输入
      setSubQuestion('');
    } catch (error) {
      console.error('展开子决策失败:', error);
      setExpansionError(error instanceof Error ? error.message : '生成子决策失败，请重试');
    } finally {
      setIsExpanding(false);
      setCurrentNodeId(null);
    }
  };

  // 计算概率色彩，概率越高颜色越绿，越低越红
  const getProbabilityColor = (probability: number): string => {
    if (probability >= 80) return 'bg-green-500';
    if (probability >= 60) return 'bg-green-400';
    if (probability >= 50) return 'bg-yellow-400';
    if (probability >= 30) return 'bg-orange-400';
    return 'bg-red-500';
  };

  // 格式化描述文本
  const formatDescription = (text: string) => {
    if (!text) return '';
    
    // 分段处理
    return text.split('\n').map((paragraph, index) => (
      <p key={index} className="mt-1 mb-2">
        {paragraph}
      </p>
    ));
  };

  return (
    <div className={`mb-6 ${level > 0 ? 'ml-6 pl-4 border-l-2 border-gray-200' : ''}`}>
      <div className="bg-white p-4 rounded-lg shadow-sm border hover:shadow-md transition-shadow decision-node">
        <div className="flex items-start">
          <div 
            className={`flex-shrink-0 w-14 h-9 ${getProbabilityColor(decision.probability)} rounded-md flex items-center justify-center text-white font-bold mr-3 probability-badge`}
          >
            {decision.probability}%
          </div>
          <div className="flex-grow">
            <h3 className="text-lg font-medium text-gray-800">{decision.title}</h3>
            <div className="text-gray-600 mt-2 mb-3 text-sm prose">
              {formatDescription(decision.description)}
            </div>
            
            {/* 注释显示区域 */}
            {notes[decision.id] && !showNotes && (
              <div className="mt-2 mb-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-md animate-fadeIn">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-700 font-medium">我的笔记</p>
                  <button 
                    onClick={() => setShowNotes(true)}
                    className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    编辑
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">{notes[decision.id]}</p>
              </div>
            )}
            
            {/* 笔记编辑区域 */}
            {showNotes && (
              <div className="mt-2 mb-3 animate-fadeIn">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">添加笔记</label>
                  <button 
                    onClick={() => setShowNotes(false)}
                    className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    完成
                  </button>
                </div>
                <textarea
                  value={noteText}
                  onChange={handleNoteChange}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm transition-colors"
                  rows={3}
                  placeholder="为这个决策添加笔记..."
                />
              </div>
            )}
            
            {/* 子问题输入区域 */}
            {showSubQuestionInput && (
              <div className="mt-3 mb-3 animate-fadeIn">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">进一步分析该决策</label>
                  <button 
                    onClick={() => setShowSubQuestionInput(false)}
                    className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    取消
                  </button>
                </div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={subQuestion}
                    onChange={(e) => setSubQuestion(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-md text-sm transition-colors"
                    placeholder="输入问题继续分析此决策..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubQuestionSubmit();
                      }
                    }}
                  />
                  <button
                    onClick={handleSubQuestionSubmit}
                    disabled={!subQuestion.trim()}
                    className="inline-flex items-center py-2 px-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    分析
                  </button>
                </div>
              </div>
            )}
            
            {/* 错误提示和重试按钮 */}
            {expansionError && (
              <div className="mt-3 mb-3 px-3 py-3 bg-red-50 border border-red-200 rounded-md animate-fadeIn">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-grow">
                    <p className="text-sm text-red-700 mb-2">{expansionError}</p>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleRetry}
                        className="inline-flex items-center py-1 px-3 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-colors"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        重试
                      </button>
                      <button
                        onClick={() => setShowSubQuestionInput(true)}
                        className="inline-flex items-center py-1 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        修改问题
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {!readOnly && (
              <div className="flex space-x-2 mt-3">
                {/* 修改展开按钮，允许为没有子选项的决策生成子选项 */}
                <button
                  onClick={handleExpand}
                  className="inline-flex items-center py-1 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <svg
                    className={`h-4 w-4 mr-1 transition-transform duration-300 ${expanded ? 'transform rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  {expanded ? '收起子选项' : decision.children.length > 0 ? '展开子选项' : '继续分析'}
                </button>
                
                {/* 重新分析按钮 - 仅对有子决策的节点显示 */}
                {decision.children.length > 0 && (
                  <button
                    onClick={handleReanalyze}
                    disabled={isReanalyzing}
                    className={`inline-flex items-center py-1 px-3 border ${isReanalyzing ? 'border-gray-200 text-gray-400' : 'border-indigo-300 text-indigo-700'} rounded-md text-sm font-medium bg-white hover:bg-indigo-50 transition-colors ${isReanalyzing ? 'cursor-not-allowed' : ''}`}
                  >
                    <svg 
                      className={`h-4 w-4 mr-1 ${isReanalyzing ? 'animate-spin' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {isReanalyzing ? '分析中...' : '重新分析'}
                  </button>
                )}
                
                {/* 添加笔记按钮 */}
                {!showNotes && (
                  <button
                    onClick={() => setShowNotes(true)}
                    className="inline-flex items-center py-1 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    {notes[decision.id] ? '编辑笔记' : '添加笔记'}
                  </button>
                )}
              </div>
            )}
            
            {readOnly && (
              <div className="flex space-x-2 mt-3">
                {decision.children.length > 0 && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="inline-flex items-center py-1 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <svg
                      className={`h-4 w-4 mr-1 transition-transform duration-300 ${expanded ? 'transform rotate-90' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    {expanded ? '收起子选项' : '展开子选项'}
                  </button>
                )}
                
                {/* 在只读模式下也添加重新分析按钮 */}
                {onExpandDecision && (
                  <button
                    onClick={handleReanalyze}
                    disabled={isReanalyzing}
                    className={`inline-flex items-center py-1 px-3 border ${isReanalyzing ? 'border-gray-200 text-gray-400' : 'border-indigo-300 text-indigo-700'} rounded-md text-sm font-medium bg-white hover:bg-indigo-50 transition-colors ${isReanalyzing ? 'cursor-not-allowed' : ''}`}
                  >
                    <svg 
                      className={`h-4 w-4 mr-1 ${isReanalyzing ? 'animate-spin' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {isReanalyzing ? '分析中...' : '重新分析'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        
        {isExpanding && currentNodeId === decision.id && (
          <div className="mt-4 w-full h-2 relative overflow-hidden bg-gray-200 rounded-full">
            <div className="animate-pulse absolute top-0 left-0 h-full bg-indigo-600 rounded-full w-full opacity-70"></div>
            <div className="absolute top-0 left-0 h-full w-16 bg-indigo-600 rounded-full animate-[loadingBar_1s_ease-in-out_infinite]"></div>
          </div>
        )}
      </div>

      {expanded && decision.children.length > 0 && (
        <div className="mt-4 animate-fadeIn">
          {decision.children.map((child) => (
            <DecisionNode
              key={child.id}
              decision={child}
              question={question}
              level={level + 1}
              readOnly={readOnly}
              notes={notes}
              onNoteChange={onNoteChange}
              onExpandDecision={onExpandDecision}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// 新的DecisionTreeView组件，支持决策笔记功能
const DecisionTreeView: React.FC = () => {
  const { decisionTree, error, loading, streamingContent, saveDecisionTree, analyzeSubDecision, setDecisionTree, generateDecisions } = useDecisionContext();
  const [notes, setNotes] = useState<DecisionNotes>(() => {
    // 尝试从localStorage加载笔记
    try {
      const savedNotes = localStorage.getItem('decisionNotes');
      return savedNotes ? JSON.parse(savedNotes) : {};
    } catch (error) {
      console.error('加载笔记失败:', error);
      return {};
    }
  });
  
  // 处理笔记变更
  const handleNoteChange = (decisionId: string, note: string) => {
    setNotes(prev => {
      const newNotes = { ...prev, [decisionId]: note };
      // 保存到localStorage
      localStorage.setItem('decisionNotes', JSON.stringify(newNotes));
      return newNotes;
    });
  };

  // 处理子决策展开
  const handleExpandDecision = async (decision: Decision, subQuestion?: string) => {
    if (subQuestion) {
      try {
        console.log('正在为决策生成子选项:', decision.title);
        console.log('子问题:', subQuestion);
        
        // 使用context中的AI服务生成子决策
        const subDecisions = await analyzeSubDecision(decision, subQuestion);
        
        // 更新决策树
        if (decisionTree && subDecisions.length > 0) {
          // 递归查找并更新决策
          const updateDecisionChildren = (decisions: Decision[]): Decision[] => {
            return decisions.map(d => {
              if (d.id === decision.id) {
                return { ...d, children: subDecisions };
              } else if (d.children.length > 0) {
                return { ...d, children: updateDecisionChildren(d.children) };
              }
              return d;
            });
          };
          
          // 创建更新后的决策树
          const updatedTree: DecisionTree = {
            ...decisionTree,
            decisions: decisionTree.decisions ? updateDecisionChildren(decisionTree.decisions) : []
          };
          
          // 更新上下文中的决策树
          setDecisionTree(updatedTree);
        }
      } catch (error) {
        console.error('生成子决策失败:', error);
        
        // 显示错误提示，而不是抛出错误
        const errorMessage = error instanceof Error ? error.message : '未知错误';
        const isApiKeyError = errorMessage.includes('API key expired');
        
        // 显示不同类型的错误消息
        if (isApiKeyError) {
          alert(`API密钥已过期，请更新API密钥后重试。\n\n您的决策树仍然安全，未丢失任何数据。`);
        } else {
          alert(`生成子决策失败: ${errorMessage}\n\n您的决策树仍然安全，未丢失任何数据。`);
        }
        
        // 不再抛出错误，只记录错误
      }
    }
  };
  
  // 获取排序后的决策
  const getSortedDecisions = (decisions: Decision[] | undefined) => {
    if (!decisions || !Array.isArray(decisions)) {
      console.warn('decisions不是有效数组', decisions);
      return [];
    }
    return [...decisions].sort((a, b) => b.probability - a.probability);
  };

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-md animate-fadeIn">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">出错了</h3>
            <div className="mt-2 text-sm text-red-700">
              {error.message}
              {error.details && (
                <p className="mt-1 text-xs">{error.details}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!decisionTree && !loading) {
    return null;
  }

  // 按概率从高到低排序决策项
  const sortedDecisions = decisionTree ? getSortedDecisions(decisionTree.decisions) : [];

  return (
    <div className="w-full max-w-4xl">
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {decisionTree ? `"${decisionTree.question}" 的决策分析` : '决策分析'}
          </h2>
          
          <div className="flex space-x-2">
            {/* 重新分析按钮 */}
            {decisionTree && decisionTree.decisions && decisionTree.decisions.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm('确定要重新分析当前决策吗？这将清除现有分析结果。')) {
                    // 重新分析当前问题
                    const currentQuestion = decisionTree.question;
                    // 使用generateDecisions重新生成决策
                    generateDecisions(currentQuestion);
                  }
                }}
                className="inline-flex items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                重新分析
              </button>
            )}
            
            {/* 保存按钮 */}
            {decisionTree && (
              <button
                onClick={saveDecisionTree}
                className="inline-flex items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" />
                </svg>
                保存当前决策
              </button>
            )}
          </div>
        </div>
        
        {loading && !decisionTree && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">正在分析...</span>
            </div>
            <div className="w-full h-2 relative overflow-hidden bg-gray-200 rounded-full">
              <div className="animate-pulse absolute top-0 left-0 h-full bg-indigo-600 rounded-full w-full opacity-70"></div>
              <div className="absolute top-0 left-0 h-full w-16 bg-indigo-600 rounded-full animate-[loadingBar_1s_ease-in-out_infinite]"></div>
            </div>
          </div>
        )}
        
        {/* 加载中状态 */}
        {loading && decisionTree && (
          <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  正在分析决策...请稍候
                </p>
              </div>
            </div>
          </div>
        )}
        
        {decisionTree && (
          <div className="space-y-4">
            {sortedDecisions.map((decision) => (
              <DecisionNode
                key={decision.id}
                decision={decision}
                question={decisionTree.question || '未命名决策'}
                level={0}
                readOnly={false}
                notes={notes}
                onNoteChange={handleNoteChange}
                onExpandDecision={handleExpandDecision}
              />
            ))}
          </div>
        )}

        {decisionTree && (!decisionTree.decisions || decisionTree.decisions.length === 0) && !loading && (
          <div className="text-center py-8 text-gray-500">
            没有发现决策选项。请尝试修改您的问题或系统提示词。
          </div>
        )}
      </div>
    </div>
  );
};

export default DecisionTreeView;

// 添加自定义动画
const style = document.createElement('style');
style.textContent = `
@keyframes loadingBar {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(400%);
  }
}
`;
document.head.appendChild(style); 